import { useState, useEffect } from 'react';
import api from '../services/api';
import { Warning, PaperPlaneTilt, X as XIcon, CalendarBlank } from '@phosphor-icons/react';

const STUFE_LABELS = { 1: 'Zahlungserinnerung', 2: '1. Mahnung', 3: '2. Mahnung', 4: 'Letzte Mahnung' };

export default function Mahnungen() {
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState({});
  const [stufe, setStufe] = useState(1);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/rent-payments/overdue', { params: { year } });
      setOverdue(res.data);
      // Default: select all with email
      const sel = {};
      res.data.forEach(r => { if (r.tenant_email) sel[r.tenant_id] = true; });
      setSelected(sel);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year]);

  const fmtEur = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const selectedIds = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
  const totalOverdue = overdue.reduce((s, r) => s + r.total_fehlt, 0);

  const send = async () => {
    if (selectedIds.length === 0) return;
    setSending(true);
    try {
      const res = await api.post('/rent-payments/mahnungen/send', {
        tenant_ids: selectedIds,
        year,
        stufe,
      });
      setResults(res.data);
      await load();
    } catch (e) {
      alert('Versand fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mahnungen-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">Mahnungen</h1>
          <p className="text-slate-500 mt-1 text-sm">Überfällige Mieten verwalten und Zahlungserinnerungen versenden</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarBlank weight="bold" className="w-5 h-5 text-slate-400" />
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="border-b border-slate-300 bg-transparent py-1 outline-none font-mono font-bold" data-testid="mahnungen-year">
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-red-50 border border-red-200 p-4">
          <p className="text-xs uppercase tracking-widest font-bold text-red-700">Offen gesamt</p>
          <p className="font-mono text-2xl font-black tracking-tighter mt-1 text-red-700">{fmtEur(totalOverdue)}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 p-4">
          <p className="text-xs uppercase tracking-widest font-bold text-amber-700">Mieter mit Rückstand</p>
          <p className="font-mono text-2xl font-black tracking-tighter mt-1 text-amber-700">{overdue.length}</p>
        </div>
        <div className="bg-slate-100 border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-700">Ausgewählt</p>
          <p className="font-mono text-2xl font-black tracking-tighter mt-1 text-slate-900">{selectedIds.length}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>
      ) : overdue.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center">
          <p className="text-slate-500">🎉 Keine überfälligen Mieten in {year}.</p>
        </div>
      ) : (
        <>
          {/* Send Bar */}
          <div className="bg-slate-900 text-white p-4 flex flex-col sm:flex-row gap-3 items-center justify-between flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm">Mahnstufe:</span>
              <select value={stufe} onChange={(e) => setStufe(parseInt(e.target.value))} className="bg-slate-700 text-white px-3 py-1.5 text-sm outline-none" data-testid="mahnstufe-select">
                {Object.entries(STUFE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <span className="text-xs text-slate-400">Mahngebühren: {stufe == 1 ? '0' : stufe == 2 ? '5' : stufe == 3 ? '10' : '20'} €</span>
            </div>
            <button
              onClick={send}
              disabled={sending || selectedIds.length === 0}
              className="bg-red-600 text-white px-5 py-2 font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2 text-sm"
              data-testid="mahnungen-send-btn"
            >
              <PaperPlaneTilt weight="bold" className="w-4 h-4" />
              {sending ? 'Sende...' : `${STUFE_LABELS[stufe]} an ${selectedIds.length} Mieter senden`}
            </button>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left w-10"></th>
                  <th className="px-3 py-2 text-left">Mieter</th>
                  <th className="px-3 py-2 text-left">E-Mail</th>
                  <th className="px-3 py-2 text-right">Offen</th>
                  <th className="px-3 py-2 text-center">Letzte Mahnstufe</th>
                  <th className="px-3 py-2 text-left">Offene Monate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {overdue.map(r => (
                  <tr key={r.tenant_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={!r.tenant_email}
                        checked={!!selected[r.tenant_id]}
                        onChange={(e) => setSelected(prev => ({ ...prev, [r.tenant_id]: e.target.checked }))}
                        data-testid={`mahnung-check-${r.tenant_id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{r.tenant_name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.tenant_email || <span className="text-red-500 text-xs">— keine E-Mail —</span>}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-red-700">{fmtEur(r.total_fehlt)}</td>
                    <td className="px-3 py-2 text-center">
                      {r.max_mahnstufe > 0 ? (
                        <span className={`text-xs px-2 py-0.5 ${r.max_mahnstufe >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {STUFE_LABELS[r.max_mahnstufe]}
                        </span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {r.items.map(i => `${String(i.month).padStart(2, '0')}/${i.year}`).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Results dialog */}
      {results && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Mahnungs-Versand abgeschlossen</h2>
              <button onClick={() => setResults(null)} className="text-slate-400 hover:text-slate-900"><XIcon weight="bold" className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-2">
              <p className="text-sm text-slate-600">{results.total} verarbeitet, {results.results.filter(r => r.status === 'sent').length} erfolgreich versendet</p>
              <div className="border max-h-64 overflow-auto">
                {results.results.map((r, i) => (
                  <div key={i} className="px-3 py-2 border-b text-sm flex items-center justify-between">
                    <div>
                      <div className="font-medium">{r.tenant_name}</div>
                      {r.reason && <div className="text-xs text-slate-500">{r.reason}</div>}
                    </div>
                    <span className={`text-xs font-bold ${r.status === 'sent' ? 'text-emerald-700' : 'text-red-700'}`}>{r.status === 'sent' ? 'Versendet ✓' : 'Fehler'}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setResults(null)} className="w-full bg-slate-900 text-white py-2 font-medium hover:bg-slate-800">Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
